import { StyleSheet, View } from 'react-native';
import { Box, Text } from '../../../components/primitives';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEventAttendee } from '../../../types/calendar';

interface EventAttendeeStackProps {
  attendees: CalendarEventAttendee[] | undefined;
  /**
   * userId -> display name, resolved by the caller from org members. The API
   * cannot supply this: `CalendarEventAttendee.userId` is a bare indexed
   * scalar, not a Prisma relation, so there is no user join to read a
   * fullName from.
   */
  displayNames?: Record<string, string>;
  testID?: string;
}

/** Beyond this the row is a smear of circles; the rest become a count. */
const MAX_VISIBLE = 3;

/**
 * Who is coming, at a glance.
 *
 * The count is spelled out in the accessible label rather than left to the
 * avatars alone — a stack of circles is not a readable guest list, and an RSVP
 * may never ride on colour.
 */
export function EventAttendeeStack({
  attendees,
  displayNames,
  testID = 'calendar_attendee_stack',
}: Readonly<EventAttendeeStackProps>) {
  if (!attendees || attendees.length === 0) return null;

  const visible = attendees.slice(0, MAX_VISIBLE);
  const overflow = attendees.length - visible.length;

  return (
    <View
      testID={testID}
      style={styles.stack}
      accessible
      accessibilityLabel={`${attendees.length} guests`}
    >
      {visible.map((attendee, index) => (
        <Box
          key={attendee.id}
          testID={`calendar_attendee_${attendee.id}`}
          style={[styles.avatar, index > 0 && styles.overlap]}
          alignItems="center"
          justifyContent="center"
        >
          <Text variant="caption" color={colors.text.inverse} style={styles.initials}>
            {initialsFor(attendee, displayNames)}
          </Text>
        </Box>
      ))}

      {overflow > 0 && (
        <Box
          testID="calendar_attendee_overflow"
          style={[styles.avatar, styles.overlap, styles.overflow]}
          alignItems="center"
          justifyContent="center"
        >
          <Text variant="caption" color={colors.text.primary} style={styles.initials}>
            {`+${overflow}`}
          </Text>
        </Box>
      )}
    </View>
  );
}

function initialsFor(
  attendee: CalendarEventAttendee,
  displayNames: Record<string, string> | undefined,
): string {
  const name = attendee.userId ? displayNames?.[attendee.userId] : undefined;

  if (name) {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : (parts[0]?.[1] ?? '');
    return `${first}${last}`.toUpperCase();
  }

  // Falls back to the email, the only identity the API returns.
  return attendee.email.slice(0, 2).toUpperCase();
}

const styles = StyleSheet.create({
  stack: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatar: {
    width: 22,
    height: 22,
    borderRadius: tokens.radius.full,
    backgroundColor: colors.primary[500],
    borderWidth: 2,
    borderColor: colors.background.primary,
  },
  // Negative margin, not a gap: the overlap is the point.
  overlap: {
    marginLeft: -6,
  },
  overflow: {
    backgroundColor: colors.surface.row,
  },
  initials: {
    fontWeight: '700',
    fontSize: 10,
  },
});
